/**
 * useFavorites — small localStorage-backed store of pinned items, keyed by
 * { kind, id }. Capped at 10 (oldest dropped). Multi-tab safe via a `storage`
 * event listener so a star toggled in one tab updates the sidebar in another.
 */
import { useCallback, useEffect, useState } from 'react';

export type FavoriteKind =
  | 'flow'
  | 'event-design'
  | 'value-mapping'
  | 'message-mapping'
  | 'connection'
  | 'runtime'
  | 'parameter';

export interface Favorite {
  kind: FavoriteKind;
  id: string;
  /** Display label captured at star-time (so the sidebar doesn't need a refetch). */
  title: string;
  /** Pinned timestamp (ISO) — for stable ordering. */
  pinnedAt: string;
}

const STORAGE_KEY = 'pipebase.favorites.v1';
const MAX_FAVORITES = 10;

function read(): Favorite[] {
  if (typeof window === 'undefined') return [];
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(
      (f) => f && typeof f.kind === 'string' && typeof f.id === 'string',
    );
  } catch {
    return [];
  }
}

function write(list: Favorite[]) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(list));
    // Notify same-tab listeners (storage events only fire across tabs).
    window.dispatchEvent(new CustomEvent('pipebase:favorites:changed'));
  } catch {
    /* ignore quota errors */
  }
}

function favKey(kind: FavoriteKind, id: string): string {
  return `${kind}::${id}`;
}

export function useFavorites() {
  const [items, setItems] = useState<Favorite[]>(() => read());

  useEffect(() => {
    const onChange = () => setItems(read());
    window.addEventListener('storage', onChange);
    window.addEventListener('pipebase:favorites:changed', onChange as EventListener);
    return () => {
      window.removeEventListener('storage', onChange);
      window.removeEventListener('pipebase:favorites:changed', onChange as EventListener);
    };
  }, []);

  const isFavorite = useCallback(
    (kind: FavoriteKind, id: string): boolean => {
      return items.some((f) => f.kind === kind && f.id === id);
    },
    [items],
  );

  const add = useCallback((fav: Omit<Favorite, 'pinnedAt'>) => {
    const list = read();
    const k = favKey(fav.kind, fav.id);
    if (list.some((f) => favKey(f.kind, f.id) === k)) return;
    const next: Favorite[] = [
      { ...fav, pinnedAt: new Date().toISOString() },
      ...list,
    ].slice(0, MAX_FAVORITES);
    write(next);
    setItems(next);
  }, []);

  const remove = useCallback((kind: FavoriteKind, id: string) => {
    const list = read();
    const next = list.filter((f) => !(f.kind === kind && f.id === id));
    if (next.length === list.length) return;
    write(next);
    setItems(next);
  }, []);

  const toggle = useCallback(
    (fav: Omit<Favorite, 'pinnedAt'>) => {
      if (isFavorite(fav.kind, fav.id)) {
        remove(fav.kind, fav.id);
      } else {
        add(fav);
      }
    },
    [add, isFavorite, remove],
  );

  return { items, isFavorite, add, remove, toggle };
}

export const __favoritesInternals = { STORAGE_KEY, MAX_FAVORITES };
